// Buyer: My Bookings widget for user dashboard
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, RefreshCw, X, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const STATUS_STYLES = {
  booked:              { bg: 'bg-emerald-500/20 border-emerald-400', text: 'Confirmed' },
  rescheduled:         { bg: 'bg-amber-500/20 border-amber-400', text: 'Rescheduled' },
  completed:           { bg: 'bg-sky-500/20 border-sky-400', text: 'Completed' },
  cancelled_by_buyer:  { bg: 'bg-rose-500/20 border-rose-400', text: 'You cancelled' },
  cancelled_by_shop:   { bg: 'bg-rose-700/20 border-rose-500', text: 'Shop cancelled · Refunded' },
  no_show:             { bg: 'bg-slate-500/20 border-slate-500', text: 'No-show' },
};

export default function MyBookingsWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('upcoming');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/marketplace/my-appointments');
      setRows(r.data.as_buyer || []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reschedule = async (id) => {
    const newDate = window.prompt('Enter new date (YYYY-MM-DD):', '');
    if (!newDate) return;
    try {
      await api.post(`/marketplace/appointment/${id}/reschedule?new_date=${newDate}`);
      toast.success('✅ Rescheduled'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this appointment?\n\n⚠️ Booking amount is NON-REFUNDABLE if you cancel.')) return;
    try {
      await api.post(`/marketplace/appointment/${id}/cancel`);
      toast.warning('❌ Cancelled · Amount non-refundable'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const downloadIcs = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL || ''}/api/marketplace/appointment/${id}/ics`, '_blank');

  const filtered = rows.filter((r) => {
    if (filter === 'upcoming') return ['booked', 'rescheduled'].includes(r.status);
    if (filter === 'past') return ['completed', 'cancelled_by_buyer', 'cancelled_by_shop', 'no_show'].includes(r.status);
    return true;
  });

  if (rows.length === 0 && !loading) return null; // Hide widget if user has no bookings

  return (
    <div className="max-w-6xl mx-auto mb-4" data-testid="my-bookings-widget">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-900/40 to-cyan-900/40 border border-emerald-400/40 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-black text-emerald-200 flex items-center gap-1">
            <Calendar size={14} /> My Bookings ({rows.length})
          </div>
          <div className="flex gap-1">
            {['upcoming', 'past', 'all'].map(k => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`my-bookings-filter-${k}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-black ${filter === k ? 'bg-emerald-400 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
                {k}
              </button>
            ))}
            <button onClick={load} data-testid="my-bookings-refresh" className="px-2 py-0.5 rounded bg-slate-700 text-[10px]">
              <RefreshCw size={10} className={loading ? 'animate-spin inline' : 'inline'} />
            </button>
          </div>
        </div>

        {filtered.length === 0 && <div className="text-center py-4 opacity-60 text-xs">No {filter} bookings</div>}

        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {filtered.map((a) => {
            const s = STATUS_STYLES[a.status] || { bg: 'bg-slate-800/40 border-slate-700', text: a.status };
            return (
              <div key={a.id} data-testid={`my-booking-${a.id.slice(0,8)}`}
                   className={`p-2 rounded-lg border ${s.bg} flex items-center gap-2 flex-wrap`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black truncate">{a.shop_name}</div>
                  <div className="text-[10px] opacity-80 flex items-center gap-1 flex-wrap">
                    <Clock size={9} /> {a.date} · {a.mode === 'virtual' ? '💻' : '🏬'} {a.mode}
                    · 💰 {a.amount_paid_at} AT paid
                    {a.amount_remaining_at > 0 && <span className="text-yellow-300">· {a.amount_remaining_at} AT due on visit</span>}
                    {a.reschedule_count > 0 && <span className="text-amber-300">· resched. once</span>}
                  </div>
                </div>
                <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-slate-900/50">{s.text}</span>
                {['booked', 'rescheduled'].includes(a.status) && (
                  <div className="flex gap-1">
                    <button onClick={() => downloadIcs(a.id)} data-testid={`my-booking-ics-${a.id.slice(0,8)}`}
                            className="px-1.5 py-0.5 rounded bg-slate-700 text-white text-[9px] font-black flex items-center gap-0.5">
                      <Download size={9} /> .ics
                    </button>
                    {a.reschedule_count === 0 && (
                      <button onClick={() => reschedule(a.id)} data-testid={`my-booking-reschedule-${a.id.slice(0,8)}`}
                              className="px-1.5 py-0.5 rounded bg-amber-500 text-slate-900 text-[9px] font-black">
                        📅 Reschedule
                      </button>
                    )}
                    <button onClick={() => cancel(a.id)} data-testid={`my-booking-cancel-${a.id.slice(0,8)}`}
                            className="px-1.5 py-0.5 rounded bg-rose-600 text-white text-[9px] font-black flex items-center gap-0.5">
                      <X size={9} /> Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
